import React, { memo } from 'react';
import { Controller } from 'react-hook-form';
import { Box, IconButton, makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import PlusCircleIcon from '../../icons/PlusCircleIcon';
import { COLOR_BLUE, COLOR_BORDER, COLOR_ORANGE, COLOR_TEXT } from '../../styles/colors';
import { EvaluationFieldProps } from '../../types/Evaluation';
import Input from '../Input';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    minWidth: 220,
    width: '100%',
  },
  inputWrapper: {
    position: 'relative',
    width: '100%',
  },
  textField: {
    width: '100%',
    '& .MuiFilledInput-root': {
      paddingRight: 36,
      alignItems: 'flex-start',
    },
    '& textarea': {
      whiteSpace: 'pre-wrap',
      overflowWrap: 'anywhere',
      wordBreak: 'break-word',
      overflowX: 'hidden',
      overflowY: 'hidden',
      resize: 'none',
    },
  },
  removeButton: {
    position: 'absolute',
    top: '50%',
    right: 4,
    transform: 'translateY(-50%)',
    padding: 4,
    color: COLOR_BORDER,
    '&:hover': {
      color: COLOR_ORANGE,
    },
  },
  addButtonWrapper: {
    marginTop: theme.spacing(1),
  },
  addButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    textDecoration: 'underline',
    fontWeight: 700,
    color: COLOR_BLUE,
    cursor: 'pointer',

    '&:hover': {
      color: COLOR_ORANGE,
    },
  },
  addButtonIcon: {
    width: 24,
    height: 24,
    color: COLOR_TEXT
  },
}), { name: 'MultiTextInput' });

function MultiTextInput({ name, control, label }: EvaluationFieldProps) {
  const classes = useStyles();

  const shouldWrapValue = (text: string): boolean => {
    if (!text) return false;

    const CHARS_PER_ROW_ESTIMATE = 55;
    return text.includes('\n') || text.length > CHARS_PER_ROW_ESTIMATE;
  };

  const getRowsForValue = (text: string): number => {
    if (!text) return 1;

    const CHARS_PER_ROW_ESTIMATE = 55;
    const explicitLineCount = text.split('\n').length;
    const estimatedLineCount = Math.ceil(text.length / CHARS_PER_ROW_ESTIMATE);

    return Math.max(1, explicitLineCount, estimatedLineCount);
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value } }) => {
        const items: string[] = Array.isArray(value) ? value : [];

        const handleAdd = () => {
          onChange([...items, '']);
        };

        const handleRemove = (index: number) => {
          onChange(items.filter((_, i) => i !== index));
        };

        const handleChange = (index: number, newValue: string) => {
          onChange(items.map((item, i) => (i === index ? newValue : item)));
        };

        return (
          <Box className={classes.container}>
            {items.map((item, index) => {
              const shouldWrap = shouldWrapValue(item);

              return (
                <Box key={index} className={classes.inputWrapper}>
                  <Input 
                      className={classes.textField}
                      label={`${label} ${index + 1}`}
                      value={item}
                      multiLine={shouldWrap}
                      rows={shouldWrap ? getRowsForValue(item) : 1}
                      onChange={(v, e) => handleChange(index, e.target.value)}
                  />
                  <IconButton className={classes.removeButton} onClick={() => handleRemove(index)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              );
            })}
            <Box className={classes.addButtonWrapper}>
              <Box className={classes.addButton} onClick={handleAdd}>
                <PlusCircleIcon className={classes.addButtonIcon} />
                Add {label}
              </Box>
            </Box>
          </Box>
        );
      }}
    />
  );
}

export default memo(MultiTextInput);
